import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Divider,
    Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";


const CartDrawer = ({ open, cart, onClose, onQtyChange, onRemove, onCheckout }) => {
    const total = cart.reduce((s, i) => s + i.totalAmount, 0);
    const itemCount = cart.reduce((s, i) => s + i.qty, 0);

    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={onClose}
            slotProps={{ paper: { sx: { borderRadius: "8px 8px 0 0", maxHeight: "80vh" } } }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, pt: 2, pb: 1.5 }}>
                    <Box>
                        <Typography variant="h6" fontWeight={600}>Your Order</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {itemCount} item{itemCount !== 1 ? "s" : ""}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ bgcolor: "action.hover", borderRadius: 2 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Divider />

                {/* Items */}
                <Box sx={{ flex: 1, overflowY: "auto" }}>
                    {cart.length === 0 ? (
                        <Box sx={{ py: 7, textAlign: "center" }}>
                            <ShoppingBagOutlinedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                            <Typography color="text.secondary" variant="body2">Cart is empty</Typography>
                        </Box>
                    ) : (
                        cart.map((item, idx) => (
                            <Box key={item.product._id}>
                                <Box sx={{ display: "flex", gap: 1.5, px: 2.5, py: 1.5, alignItems: "flex-start" }}>
                                    {/* Thumbnail placeholder */}
                                    <Box sx={{
                                        width: 56, height: 56, borderRadius: 2,
                                        bgcolor: "action.hover", flexShrink: 0,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>
                                        <ShoppingBagOutlinedIcon sx={{ color: "text.disabled", fontSize: 22 }} />
                                    </Box>

                                    {/* Product info */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={600} noWrap>
                                            {item.product.productName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {(item.product.price ?? 0).toLocaleString()} VND
                                            {item.product.unit ? ` / ${item.product.unit}` : ""}
                                        </Typography>

                                        {/* Qty stepper + line total */}
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                                            <Box sx={{
                                                display: "flex", alignItems: "center",
                                                border: "1px solid", borderColor: "divider", borderRadius: 1.5,
                                            }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => item.qty === 1 ? onRemove(idx) : onQtyChange(idx, item.qty - 1)}
                                                    sx={{ borderRadius: 0, p: 0.5, color: item.qty === 1 ? "error.main" : "text.primary" }}
                                                >
                                                    {item.qty === 1
                                                        ? <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                                        : <RemoveIcon sx={{ fontSize: 16 }} />
                                                    }
                                                </IconButton>
                                                <Typography variant="body2" sx={{ px: 1.5, fontWeight: 600, minWidth: 28, textAlign: "center" }}>
                                                    {item.qty}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onQtyChange(idx, item.qty + 1)}
                                                    sx={{ borderRadius: 0, p: 0.5 }}
                                                >
                                                    <AddIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Box>
                                            <Typography variant="body2" fontWeight={600} color="primary.main">
                                                {item.totalAmount.toLocaleString()} VND
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                {idx < cart.length - 1 && <Divider sx={{ mx: 2.5 }} />}
                            </Box>
                        ))
                    )}
                </Box>

                {/* Footer */}
                {cart.length > 0 && (
                    <Box sx={{ px: 2.5, pt: 1.5, pb: 2.5, borderTop: "1px solid", borderColor: "divider" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Total ({itemCount} item{itemCount !== 1 ? "s" : ""})
                            </Typography>
                            <Typography variant="h6" fontWeight={600} color="primary.main">
                                {total.toLocaleString()} VND
                            </Typography>
                        </Box>
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            startIcon={<ShoppingCartCheckoutIcon />}
                            onClick={() => { onClose(); onCheckout(); }}
                            sx={{ borderRadius: 2, py: 1.2 }}
                        >
                            Proceed to Checkout
                        </Button>
                    </Box>
                )}
            </Box>
        </Drawer>
    );
};

export default CartDrawer;
